<script setup lang="ts">
type HeaderSize = 'small' | 'medium' | 'large' | 'full'

withDefaults(defineProps<{
  title?: string
  description?: string
  size: HeaderSize
  breadcrumbs: boolean
  rounded?: boolean
}>(), { size: 'full', breadcrumbs: true, rounded: true })

const isBackgroundLoaded = ref(false)
</script>

<template>
  <div
    class="relative bg-hos-blue-dark overflow-hidden"
    :class="{
      'rounded-b-2xl xl:rounded-b-[5rem]': size === 'full' && rounded,
      'rounded-b-2xl xl:rounded-b-[3rem]': size === 'large' && rounded,
      'rounded-b-2xl xl:rounded-b-[2rem]': ['small', 'medium'].includes(size) && rounded,
    }"
  >
    <template v-if="['full', 'large'].includes(size)">
      <UnLazyImage
        src="/branding/background.jpg"
        alt="Background image"
        class="absolute top-0 left-0 h-full w-full object-cover transition-opacity duration-500"
        :class="isBackgroundLoaded ? 'opacity-100' : 'opacity-0'"
        :preload="false"
        :lazy-load="true"
        @loaded="isBackgroundLoaded = true"
      />
      <div class="absolute top-0 left-0 h-full w-full bg-gradient-to-r from-hos-blue-dark to-hos-blue-dark/70" />
    </template>

    <div
      class="relative"
      :class="{
        'pt-8 pb-10 md:pt-16 md:pb-24 space-y-3': size === 'full',
        'pt-4 pb-6 md:pt-7 md:pb-10 space-y-1': size === 'large',
        'pt-4 pb-6 md:pt-5 md:pb-7 space-y-1': size === 'medium',
        'pt-4 pb-6 space-y-1': size === 'small',
      }"
    >
      <CustomerModuleLayoutBreadCrumbs v-if="breadcrumbs" />
      <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div class="flex gap-4 items-center">
            <h1
              v-if="title"
              class="font-bold text-white"
              :class="{
                'text-4xl md:text-7xl': size === 'full',
                'text-3xl md:text-6xl': ['medium', 'large'].includes(size),
                'text-2xl md:text-3xl': size === 'small',
              }"
            >
              {{ title }}
            </h1>
            <slot name="titleExtra" />
          </div>
          <ClientOnly>
            <p
              v-if="description"
              class="text-hos-gray-light max-w-2xl"
              :class="{
                'text-xl': size === 'full',
                'text-lg md:text-xl': ['medium', 'large'].includes(size),
                'text-lg': size === 'small',
              }"
            >
              {{ description }}
            </p>
          </ClientOnly>
        </div>
        <slot name="headerExtra" />
      </div>
      <slot name="header" />
    </div>
  </div>
</template>
